import { Capacitor } from '@capacitor/core';

/**
 * F3 (2.1.0) — lock-screen player, iOS only for now. WKWebView (iOS 15+)
 * surfaces the Media Session API on the lock screen / control center, so the
 * 30s previews get artwork + title + play/pause controls there.
 * Pure decision + metadata builders are exported for unit tests.
 */

export interface MediaSessionMeta {
  title: string;
  artist?: string;
  artwork?: string | null;
}

/** iOS native shell only (spec: not Android for now), and only if the API exists. */
export function shouldEnableMediaSession(platform: string, hasApi: boolean): boolean {
  return platform === 'ios' && hasApi;
}

/** MediaMetadata init payload (kept pure for tests — no MediaMetadata in jsdom). */
export function buildMediaMetadataInit(meta: MediaSessionMeta): {
  title: string;
  artist: string;
  artwork: { src: string; sizes: string; type: string }[];
} {
  return {
    title: meta.title,
    artist: meta.artist ?? '',
    artwork: meta.artwork ? [{ src: meta.artwork, sizes: '512x512', type: 'image/jpeg' }] : [],
  };
}

function enabled(): boolean {
  return shouldEnableMediaSession(
    Capacitor.getPlatform(),
    typeof navigator !== 'undefined' && 'mediaSession' in navigator,
  );
}

/** Push the current track's metadata to the lock screen. */
export function updateMediaSessionMetadata(meta: MediaSessionMeta): void {
  if (!enabled()) return;
  try {
    navigator.mediaSession.metadata = new MediaMetadata(buildMediaMetadataInit(meta));
  } catch {
    // Metadata is cosmetic — never let it break playback.
  }
}

/** Reflect play/pause on the lock-screen controls. */
export function setMediaSessionPlaybackState(state: 'playing' | 'paused' | 'none'): void {
  if (!enabled()) return;
  try {
    navigator.mediaSession.playbackState = state;
  } catch {
    /* ignore */
  }
}

/** Wire the lock-screen play/pause buttons to the shared audio element. */
export function bindMediaSessionHandlers(handlers: { play: () => void; pause: () => void }): void {
  if (!enabled()) return;
  try {
    navigator.mediaSession.setActionHandler('play', handlers.play);
    navigator.mediaSession.setActionHandler('pause', handlers.pause);
  } catch {
    /* some handlers may be unsupported — ignore */
  }
}
